<template>
  <div
    ref="pickerRoot"
    :class="{ 'wall-emoji-picker-wrapper--compact': compact }"
    class="wall-emoji-picker-wrapper"
  >
    <button
      ref="trigger"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-label="copy.trigger"
      :class="{ 'wall-emoji-trigger--active': isOpen }"
      :disabled="disabled"
      :title="copy.trigger"
      aria-haspopup="dialog"
      class="wall-emoji-trigger"
      type="button"
      @click.stop="togglePicker"
    >
      <i aria-hidden="true" class="uil uil-smile wall-emoji-trigger__icon"></i>
    </button>

    <transition name="wall-emoji-picker">
      <div
        v-if="isOpen"
        :aria-label="copy.title"
        class="wall-emoji-picker"
        role="dialog"
        @click.stop
      >
        <div class="wall-emoji-picker__controls">
          <div class="wall-emoji-picker__search" role="search">
            <i aria-hidden="true" class="uil uil-search"></i>
            <input
              ref="searchInput"
              v-model="search"
              :aria-label="copy.search"
              :placeholder="copy.search"
              autocomplete="off"
              type="search"
            />
            <button
              v-if="search"
              :aria-label="copy.clear"
              :title="copy.clear"
              type="button"
              @click="search = ''"
            >
              <i class="uil uil-times"></i>
            </button>
          </div>

          <button
            :aria-label="copy.close"
            :title="copy.close"
            class="wall-emoji-picker__close"
            type="button"
            @click="closePicker"
          >
            <i class="uil uil-times"></i>
          </button>
        </div>

        <div class="wall-emoji-picker__content">
          <section
            v-for="group in filteredGroups"
            :key="group.id"
            class="wall-emoji-picker__group"
          >
            <h6>
              <span aria-hidden="true">{{ group.icon }}</span>
              {{ group.label }}
            </h6>
            <div class="wall-emoji-picker__grid">
              <button
                v-for="emoji in group.emojis"
                :key="group.id + '-' + emoji.value"
                :aria-label="emoji.name"
                :title="emoji.name"
                type="button"
                @click="selectEmoji(emoji.value)"
              >
                <span aria-hidden="true">{{ emoji.value }}</span>
              </button>
            </div>
          </section>

          <div v-if="!hasResults" class="wall-emoji-picker__empty">
            <i class="uil uil-search-alt"></i>
            <strong>{{ copy.empty }}</strong>
          </div>
        </div>

        <div class="wall-emoji-picker__footer">
          <i class="uil uil-mouse-alt"></i>
          <span>{{ copy.hint }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
const EMOJI_GROUPS = [
  {
    id: "popular",
    icon: "✨",
    emojis: [
      { value: "😀", name: "Gülen yüz", search: "smile happy mutlu" },
      { value: "😂", name: "Kahkahalı yüz", search: "laugh komik gülmek" },
      { value: "❤️", name: "Kalp", search: "heart love sevgi" },
      { value: "👍", name: "Beğen", search: "like thumb tamam" },
      { value: "🎉", name: "Kutlama", search: "party celebration tebrik" },
      { value: "🔥", name: "Ateş", search: "fire hot harika" },
      { value: "🚀", name: "Roket", search: "rocket launch başarı" },
      { value: "👏", name: "Alkış", search: "clap applause bravo" },
    ],
  },
  {
    id: "work",
    icon: "💼",
    emojis: [
      {
        value: "💼",
        name: "İş çantası",
        search: "briefcase work business iş kariyer",
      },
      {
        value: "🏢",
        name: "Ofis binası",
        search: "office company corporate şirket kurum ofis",
      },
      {
        value: "🧑‍💼",
        name: "Ofis çalışanı",
        search: "professional employee colleague çalışan yönetici",
      },
      {
        value: "👥",
        name: "Ekip",
        search: "team people collaboration ekip takım işbirliği",
      },
      {
        value: "💻",
        name: "Dizüstü bilgisayar",
        search: "laptop computer remote bilgisayar çalışma",
      },
      {
        value: "🖥️",
        name: "Masaüstü bilgisayar",
        search: "desktop screen workstation ekran bilgisayar",
      },
      {
        value: "📧",
        name: "E-posta",
        search: "email mail inbox posta iletişim",
      },
      {
        value: "📞",
        name: "Telefon görüşmesi",
        search: "phone call görüşme arama iletişim",
      },
      {
        value: "💬",
        name: "Mesaj",
        search: "message chat comment mesaj sohbet yorum",
      },
      {
        value: "🎥",
        name: "Video toplantı",
        search: "video meeting camera webinar toplantı görüşme",
      },
      {
        value: "🎙️",
        name: "Sunum",
        search: "microphone presentation speech sunum konuşma",
      },
      {
        value: "🌐",
        name: "Küresel çalışma",
        search: "global world international küresel dünya uluslararası",
      },
      {
        value: "🏠",
        name: "Uzaktan çalışma",
        search: "home remote hybrid ev uzaktan hibrit çalışma",
      },
      {
        value: "🪪",
        name: "Çalışan kimliği",
        search: "identity badge card employee kimlik yaka kartı",
      },
    ],
  },
  {
    id: "planning",
    icon: "📋",
    emojis: [
      {
        value: "📋",
        name: "Görev listesi",
        search: "clipboard checklist task görev liste plan",
      },
      {
        value: "🗓️",
        name: "Ajanda",
        search: "calendar schedule agenda takvim planlama toplantı",
      },
      {
        value: "⏰",
        name: "Son tarih",
        search: "alarm deadline time reminder saat son tarih",
      },
      {
        value: "⏳",
        name: "Süreç devam ediyor",
        search: "hourglass progress pending süreç devam bekliyor",
      },
      {
        value: "📎",
        name: "Ek dosya",
        search: "paperclip attachment ek dosya ilişik",
      },
      {
        value: "📁",
        name: "Dosya klasörü",
        search: "folder file dosya klasör belge",
      },
      {
        value: "🗂️",
        name: "Arşiv",
        search: "archive organize index arşiv düzen klasör",
      },
      {
        value: "📄",
        name: "Belge",
        search: "document page report belge doküman rapor",
      },
      {
        value: "📚",
        name: "Kaynaklar",
        search: "books resources training bilgi kaynak eğitim",
      },
      {
        value: "🔍",
        name: "İnceleme",
        search: "search review inspect research inceleme araştırma",
      },
      {
        value: "🔔",
        name: "Hatırlatma",
        search: "bell notification reminder bildirim hatırlatma",
      },
      {
        value: "🚩",
        name: "Kilometre taşı",
        search: "flag milestone checkpoint hedef aşama",
      },
      {
        value: "🧭",
        name: "Yol haritası",
        search: "compass roadmap direction yol haritası yön strateji",
      },
      {
        value: "🧩",
        name: "Çözüm parçası",
        search: "puzzle solution integration çözüm parça entegrasyon",
      },
    ],
  },
  {
    id: "status",
    icon: "🟢",
    emojis: [
      {
        value: "🟢",
        name: "Yolunda",
        search: "green on track ready yolunda hazır başarılı",
      },
      {
        value: "🟡",
        name: "Dikkat gerekiyor",
        search: "yellow attention warning dikkat takip",
      },
      {
        value: "🟠",
        name: "Riskli",
        search: "orange risk at risk riskli sorun",
      },
      {
        value: "🔴",
        name: "Kritik",
        search: "red critical urgent kritik acil",
      },
      {
        value: "🔵",
        name: "Bilgilendirme",
        search: "blue information update bilgi duyuru",
      },
      {
        value: "⚪",
        name: "Beklemede",
        search: "white waiting pending beklemede sırada",
      },
      {
        value: "🔄",
        name: "Devam ediyor",
        search: "ongoing refresh sync progress devam süreç",
      },
      {
        value: "⏸️",
        name: "Duraklatıldı",
        search: "pause paused hold duraklatıldı askıda",
      },
      {
        value: "⛔",
        name: "Engellendi",
        search: "blocked stop impediment engel engellendi",
      },
      { value: "🆕", name: "Yeni", search: "new latest yeni güncel" },
      {
        value: "❗",
        name: "Acil",
        search: "urgent important exclamation acil önemli",
      },
      {
        value: "❓",
        name: "Soru",
        search: "question help soru yardım açıklama",
      },
      {
        value: "➡️",
        name: "Sonraki adım",
        search: "next step arrow sonraki adım ileri",
      },
      {
        value: "🔜",
        name: "Yakında",
        search: "soon upcoming yakında gelecek",
      },
    ],
  },
  {
    id: "results",
    icon: "📈",
    emojis: [
      {
        value: "📈",
        name: "Büyüme",
        search: "growth increase chart büyüme artış yükseliş",
      },
      {
        value: "📉",
        name: "Düşüş",
        search: "decrease decline chart düşüş azalış",
      },
      {
        value: "💹",
        name: "Performans",
        search: "performance market metric performans sonuç",
      },
      {
        value: "🧮",
        name: "Hesaplama",
        search: "abacus calculation budget hesaplama bütçe",
      },
      {
        value: "💰",
        name: "Bütçe",
        search: "budget money finance bütçe finans",
      },
      {
        value: "💵",
        name: "Gelir",
        search: "revenue income cash gelir kazanç",
      },
      {
        value: "🧾",
        name: "Gider ve fatura",
        search: "receipt invoice expense gider fatura masraf",
      },
      {
        value: "🏦",
        name: "Finans",
        search: "bank finance institution banka finans kurum",
      },
      {
        value: "📦",
        name: "Teslimat",
        search: "package delivery shipment teslimat sevkiyat ürün",
      },
      {
        value: "🏭",
        name: "Üretim",
        search: "factory production operation üretim operasyon",
      },
      {
        value: "🌱",
        name: "Sürdürülebilirlik",
        search: "sustainability growth green sürdürülebilirlik çevre",
      },
      {
        value: "🧠",
        name: "Strateji",
        search: "brain strategy thinking strateji fikir plan",
      },
      {
        value: "🔬",
        name: "Araştırma",
        search: "research analysis laboratory araştırma analiz",
      },
      {
        value: "⚙️",
        name: "Süreç",
        search: "gear process settings operation süreç operasyon ayar",
      },
    ],
  },
  {
    id: "expressions",
    icon: "😊",
    emojis: [
      { value: "😊", name: "Mutlu yüz", search: "happy smile memnun" },
      { value: "😍", name: "Aşık yüz", search: "love heart eyes sevgi" },
      { value: "🥰", name: "Sevgi dolu yüz", search: "love hearts sarılma" },
      { value: "🤩", name: "Yıldız gözlü", search: "star excited hayran" },
      { value: "🥳", name: "Parti yüzü", search: "party birthday kutlama" },
      { value: "😎", name: "Havalı yüz", search: "cool sunglasses güneş" },
      { value: "🤔", name: "Düşünen yüz", search: "thinking question fikir" },
      { value: "😉", name: "Göz kırpan yüz", search: "wink playful şaka" },
      { value: "😅", name: "Terleyen yüz", search: "sweat relief rahatlama" },
      { value: "😇", name: "Melek yüz", search: "angel innocent masum" },
      { value: "😮", name: "Şaşırmış yüz", search: "surprise wow şaşkın" },
      {
        value: "🤯",
        name: "Aklı uçmuş yüz",
        search: "mind blown shocked şaşkın",
      },
      { value: "😢", name: "Üzgün yüz", search: "sad cry üzgün" },
      { value: "😭", name: "Ağlayan yüz", search: "cry tears ağlamak" },
      { value: "😡", name: "Kızgın yüz", search: "angry mad sinirli" },
      { value: "😴", name: "Uyuyan yüz", search: "sleep tired yorgun" },
    ],
  },
  {
    id: "gestures",
    icon: "🙌",
    emojis: [
      { value: "👋", name: "El sallama", search: "wave hello merhaba" },
      {
        value: "🙌",
        name: "Kutlayan eller",
        search: "hooray celebrate başarı",
      },
      {
        value: "🙏",
        name: "Birleşmiş eller",
        search: "thanks please teşekkür",
      },
      { value: "💪", name: "Güçlü kol", search: "strong muscle güç" },
      { value: "👌", name: "Tamam işareti", search: "ok perfect tamam" },
      { value: "✌️", name: "Zafer işareti", search: "victory peace barış" },
      {
        value: "🤝",
        name: "El sıkışma",
        search: "handshake agreement anlaşma",
      },
      { value: "🤞", name: "Şans dileme", search: "luck crossed şans" },
      { value: "🤗", name: "Sarılma", search: "hug support destek" },
      { value: "🤲", name: "Açık eller", search: "open hands sunmak" },
      { value: "☝️", name: "Yukarı işaret", search: "point up yukarı" },
      { value: "👉", name: "Sağa işaret", search: "point right sağ" },
      { value: "👈", name: "Sola işaret", search: "point left sol" },
      { value: "👎", name: "Beğenmeme", search: "dislike thumb down hayır" },
    ],
  },
  {
    id: "symbols",
    icon: "💡",
    emojis: [
      {
        value: "🔹",
        name: "Küçük mavi elmas",
        search: "small blue diamond madde",
      },
      {
        value: "🔸",
        name: "Küçük turuncu elmas",
        search: "small orange diamond madde",
      },
      { value: "✅", name: "Onay işareti", search: "check done tamamlandı" },
      { value: "❌", name: "Çarpı işareti", search: "cross error hata" },
      { value: "⚠️", name: "Uyarı", search: "warning attention dikkat" },
      { value: "⭐", name: "Yıldız", search: "star favorite favori" },
      { value: "✨", name: "Parıltı", search: "sparkles new yeni" },
      { value: "💯", name: "Yüz puan", search: "hundred perfect mükemmel" },
      { value: "🎯", name: "Hedef", search: "target goal amaç" },
      { value: "💡", name: "Fikir", search: "idea bulb ışık" },
      { value: "📌", name: "Raptiye", search: "pin important önemli" },
      { value: "📣", name: "Duyuru", search: "announcement megaphone ilan" },
      { value: "📝", name: "Not", search: "memo note yazı" },
      { value: "📅", name: "Takvim", search: "calendar date tarih" },
      { value: "📊", name: "Grafik", search: "chart report rapor" },
      { value: "🔗", name: "Bağlantı", search: "link chain zincir" },
      { value: "🏆", name: "Kupa", search: "trophy winner kazanan" },
      { value: "🥇", name: "Altın madalya", search: "medal first birinci" },
      { value: "☕", name: "Kahve", search: "coffee break mola" },
      { value: "🎁", name: "Hediye", search: "gift present sürpriz" },
    ],
  },
];

const CORPORATE_GROUP_IDS = ["work", "planning", "status", "results"];
const ORDERED_EMOJI_GROUPS = [
  ...EMOJI_GROUPS.filter((group) => !CORPORATE_GROUP_IDS.includes(group.id)),
  ...EMOJI_GROUPS.filter((group) => CORPORATE_GROUP_IDS.includes(group.id)),
];

const PICKER_COPY = {
  tr: {
    trigger: "Emoji ekle",
    title: "Emoji seç",
    search: "Emoji ara",
    close: "Emoji seçiciyi kapat",
    clear: "Aramayı temizle",
    empty: "Emoji bulunamadı",
    hint: "Emoji, imlecin bulunduğu yere eklenir.",
    groups: {
      popular: "Popüler",
      work: "İş ve iletişim",
      planning: "Planlama",
      status: "Proje durumu",
      results: "Veri ve sonuçlar",
      expressions: "İfadeler",
      gestures: "Jestler",
      symbols: "Semboller ve nesneler",
    },
  },
  en: {
    trigger: "Add emoji",
    title: "Choose an emoji",
    search: "Search emoji",
    close: "Close emoji picker",
    clear: "Clear search",
    empty: "No emoji found",
    hint: "The emoji is inserted at the cursor position.",
    groups: {
      popular: "Popular",
      work: "Work and communication",
      planning: "Planning",
      status: "Project status",
      results: "Data and results",
      expressions: "Expressions",
      gestures: "Gestures",
      symbols: "Symbols and objects",
    },
  },
  az: {
    trigger: "Emoji əlavə et",
    title: "Emoji seç",
    search: "Emoji axtar",
    close: "Emoji seçicini bağla",
    clear: "Axtarışı təmizlə",
    empty: "Emoji tapılmadı",
    hint: "Emoji kursorun olduğu yerə əlavə edilir.",
    groups: {
      popular: "Populyar",
      work: "İş və ünsiyyət",
      planning: "Planlama",
      status: "Layihə statusu",
      results: "Məlumat və nəticələr",
      expressions: "İfadələr",
      gestures: "Jestlər",
      symbols: "Simvollar və obyektlər",
    },
  },
  fr: {
    trigger: "Ajouter un emoji",
    title: "Choisir un emoji",
    search: "Rechercher un emoji",
    close: "Fermer le sélecteur d’emoji",
    clear: "Effacer la recherche",
    empty: "Aucun emoji trouvé",
    hint: "L’emoji est inséré à la position du curseur.",
    groups: {
      popular: "Populaires",
      work: "Travail et communication",
      planning: "Planification",
      status: "État du projet",
      results: "Données et résultats",
      expressions: "Expressions",
      gestures: "Gestes",
      symbols: "Symboles et objets",
    },
  },
  es: {
    trigger: "Añadir emoji",
    title: "Elegir un emoji",
    search: "Buscar emoji",
    close: "Cerrar selector de emojis",
    clear: "Borrar búsqueda",
    empty: "No se encontraron emojis",
    hint: "El emoji se inserta en la posición del cursor.",
    groups: {
      popular: "Populares",
      work: "Trabajo y comunicación",
      planning: "Planificación",
      status: "Estado del proyecto",
      results: "Datos y resultados",
      expressions: "Expresiones",
      gestures: "Gestos",
      symbols: "Símbolos y objetos",
    },
  },
};

function normalizeSearch(value) {
  const normalizedValue = String(value || "")
    .trim()
    .toLocaleLowerCase();

  return typeof normalizedValue.normalize === "function"
    ? normalizedValue.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    : normalizedValue;
}

export default {
  name: "WallEmojiPicker",
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      search: "",
    };
  },
  computed: {
    copy() {
      const locale = String(this.$i18n?.locale || "tr")
        .toLowerCase()
        .split(/[-_]/)[0];
      return PICKER_COPY[locale] || PICKER_COPY.en;
    },
    filteredGroups() {
      const query = normalizeSearch(this.search);

      return ORDERED_EMOJI_GROUPS.map((group) => ({
        ...group,
        label: this.copy.groups[group.id],
        emojis: query
          ? group.emojis.filter((emoji) =>
              normalizeSearch(
                `${emoji.name} ${emoji.search} ${group.id} ${
                  this.copy.groups[group.id]
                }`
              ).includes(query)
            )
          : group.emojis,
      })).filter((group) => group.emojis.length);
    },
    hasResults() {
      return this.filteredGroups.length > 0;
    },
  },
  watch: {
    disabled(isDisabled) {
      if (isDisabled && this.isOpen) this.closePicker();
    },
  },
  mounted() {
    document.addEventListener("click", this.handleDocumentClick);
    window.addEventListener("keydown", this.handleKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleDocumentClick);
    window.removeEventListener("keydown", this.handleKeydown);
  },
  methods: {
    togglePicker() {
      if (this.disabled) return;

      if (this.isOpen) {
        this.closePicker();
        return;
      }

      this.$emit("open");
      this.isOpen = true;
      this.$nextTick(() => this.$refs.searchInput?.focus());
    },
    closePicker() {
      this.isOpen = false;
      this.search = "";
    },
    selectEmoji(emoji) {
      this.$emit("select", emoji);
    },
    handleDocumentClick(event) {
      if (!this.isOpen || this.$refs.pickerRoot?.contains(event.target)) return;
      this.closePicker();
    },
    handleKeydown(event) {
      if (event.key !== "Escape" || !this.isOpen) return;

      this.closePicker();
      this.$nextTick(() => this.$refs.trigger?.focus());
    },
  },
};
</script>

<style scoped>
.wall-emoji-picker-wrapper,
.wall-emoji-picker-wrapper * {
  box-sizing: border-box;
}

.wall-emoji-picker-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  z-index: 20;
}

.wall-emoji-trigger {
  --iconColor: #536174;
  display: inline-flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #536174;
  cursor: pointer;
  background: linear-gradient(145deg, #ffffff, #f3f6fa);
  border: 1px solid #dde5ef;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.06);
  transition: color 160ms ease, border-color 160ms ease, background 160ms ease,
    box-shadow 160ms ease, transform 160ms ease;
}

.wall-emoji-picker-wrapper--compact .wall-emoji-trigger {
  --iconColor: #536174;
  width: 38px;
  height: 38px;
  color: #536174;
  background: #ffffff;
  border-color: #dbe2eb;
  border-radius: 8px;
  box-shadow: none;
}

.wall-emoji-trigger__icon {
  color: inherit;
  font-size: 21px;
  line-height: 1;
}

.wall-emoji-picker-wrapper--compact .wall-emoji-trigger__icon {
  font-size: 19px;
}

.wall-emoji-trigger:not(:disabled):hover,
.wall-emoji-trigger:focus-visible,
.wall-emoji-trigger--active {
  --iconColor: #0a66c2;
  color: #0a66c2;
  background: #eef6ff;
  border-color: #8fc2f3;
  box-shadow: 0 0 0 3px rgba(10, 102, 194, 0.11);
  outline: none;
  transform: translateY(-1px);
}

.wall-emoji-picker-wrapper--compact .wall-emoji-trigger:not(:disabled):hover,
.wall-emoji-picker-wrapper--compact .wall-emoji-trigger:focus-visible,
.wall-emoji-picker-wrapper--compact .wall-emoji-trigger--active {
  --iconColor: #3f9830;
  color: #3f9830;
  background: #f1f8ef;
  border-color: #cfe5ca;
  box-shadow: 0 0 0 3px rgba(80, 184, 62, 0.1);
}

.wall-emoji-trigger:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.wall-emoji-picker {
  position: absolute;
  top: calc(100% + 12px);
  left: 0;
  width: 348px;
  max-width: calc(100vw - 32px);
  overflow: visible;
  color: #172033;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid #dce5ef;
  border-radius: 16px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.18),
    0 3px 10px rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(14px);
  transform-origin: top left;
}

.wall-emoji-picker-wrapper--compact .wall-emoji-picker {
  top: calc(100% + 8px);
  left: -8px;
}

.wall-emoji-picker::before {
  position: absolute;
  top: -6px;
  left: 17px;
  width: 12px;
  height: 12px;
  content: "";
  background: #f8fafc;
  border-top: 1px solid #dce5ef;
  border-left: 1px solid #dce5ef;
  transform: rotate(45deg);
}

.wall-emoji-picker__controls {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px 10px;
  background: #f8fafc;
  border-bottom: 1px solid #e7edf4;
  border-radius: 15px 15px 0 0;
}

.wall-emoji-picker__close {
  display: inline-flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  margin: 0 !important;
  padding: 0;
  color: #697586;
  font-size: 18px;
  cursor: pointer;
  background: #ffffff;
  border: 1px solid #dde4ec;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: color 150ms ease, background 150ms ease, border-color 150ms ease;
}

.wall-emoji-picker__close:hover,
.wall-emoji-picker__close:focus-visible {
  color: #dc3545;
  background: #fff5f6;
  border-color: #f1b8be;
  outline: none;
}

.wall-emoji-picker__search {
  position: relative;
  display: flex;
  min-width: 0;
  flex: 1;
  height: 40px;
  align-items: center;
  margin: 0;
  color: #8995a6;
}

.wall-emoji-picker__search > i {
  position: absolute;
  left: 12px;
  z-index: 1;
  font-size: 16px;
  pointer-events: none;
}

.wall-emoji-picker__search input {
  display: block;
  width: 100%;
  height: 40px;
  min-height: 40px;
  margin: 0 !important;
  padding: 0 38px 0 36px;
  color: #283548;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  background: #f6f8fb;
  border: 1px solid #e1e7ef;
  border-radius: 11px;
  outline: none;
  transition: background 150ms ease, border-color 150ms ease,
    box-shadow 150ms ease;
}

.wall-emoji-picker__search input:focus {
  background: #ffffff;
  border-color: #8fc2f3;
  box-shadow: 0 0 0 3px rgba(10, 102, 194, 0.09);
}

.wall-emoji-picker__search > button {
  position: absolute;
  right: 7px;
  display: inline-flex;
  width: 26px;
  height: 26px;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #8b96a6;
  font-size: 13px;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 7px;
}

.wall-emoji-picker__search > button:hover,
.wall-emoji-picker__search > button:focus-visible {
  color: #0a66c2;
  background: #eaf3fd;
  outline: none;
}

.wall-emoji-picker__content {
  max-height: 264px;
  padding: 2px 10px 10px 14px;
  overflow-y: auto;
  scrollbar-color: #c5d0dd transparent;
  scrollbar-width: thin;
}

.wall-emoji-picker__content::-webkit-scrollbar {
  width: 5px;
}

.wall-emoji-picker__content::-webkit-scrollbar-thumb {
  background: #c5d0dd;
  border-radius: 999px;
}

.wall-emoji-picker__group {
  margin-top: 9px;
}

.wall-emoji-picker__group h6 {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 7px;
  color: #758195;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.045em;
  text-transform: uppercase;
}

.wall-emoji-picker__group h6 span {
  font-size: 12px;
}

.wall-emoji-picker__grid {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: 4px;
}

.wall-emoji-picker__grid button {
  display: inline-flex;
  width: 100%;
  height: 36px;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji",
    sans-serif;
  font-size: 21px;
  line-height: 1;
  cursor: pointer;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 10px;
  transition: background 120ms ease, border-color 120ms ease,
    box-shadow 120ms ease, transform 120ms ease;
}

.wall-emoji-picker__grid button:hover,
.wall-emoji-picker__grid button:focus-visible {
  background: #edf6ff;
  border-color: #c6ddf5;
  box-shadow: 0 3px 8px rgba(10, 102, 194, 0.12);
  outline: none;
  transform: translateY(-2px) scale(1.07);
}

.wall-emoji-picker__empty {
  display: flex;
  min-height: 142px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 9px;
  color: #8a96a8;
  text-align: center;
}

.wall-emoji-picker__empty i {
  font-size: 28px;
}

.wall-emoji-picker__empty strong {
  font-size: 12px;
  font-weight: 600;
}

.wall-emoji-picker__footer {
  display: flex;
  min-height: 36px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  color: #8a96a8;
  font-size: 9.5px;
  background: #fafbfd;
  border-top: 1px solid #edf1f5;
  border-radius: 0 0 15px 15px;
}

.wall-emoji-picker__footer i {
  color: #0a66c2;
  font-size: 13px;
}

.wall-emoji-picker-enter-active,
.wall-emoji-picker-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
}

.wall-emoji-picker-enter,
.wall-emoji-picker-leave-to {
  opacity: 0;
  transform: translateY(-7px) scale(0.98);
}

@media (max-width: 480px) {
  .wall-emoji-picker {
    width: 328px;
    max-width: calc(100vw - 24px);
  }

  .wall-emoji-picker__grid {
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }
}

@media (prefers-reduced-motion: reduce) {
  .wall-emoji-trigger,
  .wall-emoji-picker__grid button,
  .wall-emoji-picker-enter-active,
  .wall-emoji-picker-leave-active {
    transition: none;
  }
}
</style>
